import React, { useEffect, useState } from "react";
import {
  FLEET_FIRST_MACHINE_NO,
  FLEET_LAST_MACHINE_NO,
  FLEET_TARGET_MACHINES
} from "../constants/fleet";
import API from "../services/api";
import { formatDateTimeDMY } from "../utils/dateFormat";

function Qibixx() {
  const [devices, setDevices] = useState([]);
  const [machines, setMachines] = useState([]);
  const [newName, setNewName] = useState("");
  const [newMachineId, setNewMachineId] = useState("");
  const [creating, setCreating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState("");
  const [history, setHistory] = useState([]);
  const [historyDeviceId, setHistoryDeviceId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadDevices = async () => {
    const [d, m] = await Promise.all([API.get("/devices"), API.get("/machines")]);
    setDevices(d.data);
    setMachines(m.data);
  };

  useEffect(() => {
    loadDevices();
    const t = setInterval(loadDevices, 30000);
    return () => clearInterval(t);
  }, []);

  const createTerminal = async () => {
    if (!newName.trim()) {
      alert("Ingresá un nombre para el terminal (ej. Máquina 12, Qibixx-A1).");
      return;
    }
    setCreating(true);
    try {
      const res = await API.post("/devices", {
        name: newName.trim(),
        machine_id: newMachineId ? parseInt(newMachineId, 10) : null
      });
      setGeneratedToken(res.data.token || "");
      setNewName("");
      setNewMachineId("");
      await loadDevices();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo crear el terminal.");
    } finally {
      setCreating(false);
    }
  };

  const saveRegistrationId = async (d, value) => {
    await API.put(`/devices/${d.id}`, {
      name: d.name,
      machine_id: d.machine_id,
      active: d.active,
      qibixx_registration_id: value.trim() || null
    });
    await loadDevices();
  };

  const loadHistory = async deviceId => {
    setLoadingHistory(true);
    setHistoryDeviceId(deviceId);
    try {
      const res = await API.get("/devices/qibixx/history", {
        params: { device_id: deviceId, limit: 40 }
      });
      setHistory(res.data);
    } catch (e) {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fmt = v => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Sí" : "No";
    return String(v);
  };

  const activeDevices = devices.filter(d => d.active !== false);

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Monitor Qibixx</h1>
        <p className="page-subtitle">
          ID de registro Linuquick, último estado USB desde la app Android y trazas recientes.
          Podés pegar el ID que entregue el proveedor en cada terminal. Plan operativo:{" "}
          <strong>
            {FLEET_TARGET_MACHINES} máquinas (N° {FLEET_FIRST_MACHINE_NO}–{FLEET_LAST_MACHINE_NO}) →{" "}
            {FLEET_TARGET_MACHINES} terminales
          </strong>{" "}
          (uno por máquina); creá cada terminal con el botón de abajo y configurá el token en el
          Android correspondiente. El <code>machine_id</code> en el backend debe coincidir con el N°
          de máquina.
        </p>
      </header>

      <div className="card card--padded" style={{ marginBottom: "1.25rem" }}>
        <h2 className="section-title">Crear terminal</h2>
        <p style={{ fontSize: "0.9rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
          Cada terminal = un dispositivo en el backend (mismo modelo que en Dispositivos). Al crear
          se genera un <strong>token</strong> único para configurar en la app.
        </p>
        <div className="field-row" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
          <input
            className="input"
            placeholder="Nombre del terminal (ej. Qibixx-042)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            style={{ minWidth: "220px", flex: "1 1 200px" }}
          />
          <select
            className="select"
            value={newMachineId}
            onChange={e => setNewMachineId(e.target.value)}
            style={{ minWidth: "200px" }}
          >
            <option value="">Sin máquina asignada (opcional)</option>
            {machines.map(m => (
              <option key={m.id} value={m.id}>
                {m.name} (id {m.id})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={createTerminal}
            disabled={creating}
          >
            {creating ? "Creando…" : "Crear"}
          </button>
        </div>
      </div>

      {generatedToken && (
        <div className="alert alert--warning" style={{ marginBottom: "1.25rem" }}>
          <strong>Token del nuevo terminal (mostrar solo una vez; copiar al Android)</strong>
          <div className="token-box">{generatedToken}</div>
        </div>
      )}

      <div className="table-wrap">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Terminal</th>
              <th>Máquina</th>
              <th>ID registro Qibixx</th>
              <th>USB (último reporte)</th>
              <th>Firmware (hint)</th>
              <th>Último reporte</th>
              <th>Estado (recorte)</th>
              <th>Error</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {activeDevices.map(d => (
              <tr key={d.id}>
                <td>
                  <strong>{d.name}</strong>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                    id #{d.id}
                  </div>
                </td>
                <td>{d.machine_name || "—"}</td>
                <td style={{ maxWidth: "220px" }}>
                  <input
                    className="input"
                    defaultValue={d.qibixx_registration_id || ""}
                    placeholder="Pegar ID proveedor"
                    onBlur={e => {
                      const v = e.target.value.trim();
                      const cur = (d.qibixx_registration_id || "").trim();
                      if (v !== cur) {
                        saveRegistrationId(d, v);
                      }
                    }}
                  />
                </td>
                <td>{fmt(d.qibixx_usb_connected)}</td>
                <td style={{ fontSize: "0.85rem", maxWidth: "140px", wordBreak: "break-all" }}>
                  {fmt(d.qibixx_firmware_version)}
                </td>
                <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                  {d.qibixx_last_report_at
                    ? formatDateTimeDMY(d.qibixx_last_report_at)
                    : "—"}
                </td>
                <td style={{ fontSize: "0.8rem", maxWidth: "280px", wordBreak: "break-word" }}>
                  {d.qibixx_last_status
                    ? d.qibixx_last_status.slice(0, 400)
                    : "—"}
                </td>
                <td style={{ fontSize: "0.8rem", maxWidth: "200px", color: "var(--color-danger, #c44)" }}>
                  {fmt(d.qibixx_last_error)}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => loadHistory(d.id)}
                  >
                    Historial
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {historyDeviceId != null && (
        <div className="card card--padded" style={{ marginTop: "1.5rem" }}>
          <h2 className="section-title">
            Trazas recientes (dispositivo #{historyDeviceId})
            {loadingHistory ? " …" : ""}
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            Cada fila es un POST de telemetría desde Android (estado USB, línea de estado, etc.).
          </p>
          <div className="table-wrap">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Payload JSON</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && !loadingHistory ? (
                  <tr>
                    <td colSpan={2}>Sin datos aún.</td>
                  </tr>
                ) : (
                  history.map(row => (
                    <tr key={row.id}>
                      <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                        {formatDateTimeDMY(row.created_at)}
                      </td>
                      <td>
                        <pre
                          style={{
                            margin: 0,
                            fontSize: "0.75rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "120px",
                            overflow: "auto"
                          }}
                        >
                          {typeof row.payload === "object"
                            ? JSON.stringify(row.payload, null, 2)
                            : String(row.payload)}
                        </pre>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Qibixx;
